import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle2, Star, Quote, Phone, BatteryCharging, Truck, CircleDot, Fuel, Car, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services, testimonials, stats, brands } from '../mock';
import * as LucideIcons from 'lucide-react';

const Home = () => {
  const { scrollYProgress } = useScroll();
  const [displayText, setDisplayText] = useState('');
  const fullText = 'Drive in with problems & Drive out with peace of mind.';

  // Kinetic text reveal animation
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const getIconComponent = (iconName) => {
    const iconMap = {
      wrench: LucideIcons.Wrench,
      sparkles: LucideIcons.Sparkles,
      paintbrush: LucideIcons.Paintbrush,
      wind: LucideIcons.Wind,
      shield: LucideIcons.Shield,
      fileText: LucideIcons.FileText,
      truck: LucideIcons.Truck,
      zap: LucideIcons.Zap
    };
    return iconMap[iconName] || LucideIcons.Wrench;
  };

  return (
    <div className="bg-white">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax */}
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1618642624018-a370cbf3cd80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjB3b3Jrc2hvcHxlbnwwfHx8fDE3ODQ5Njg3OTB8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-red-900/50" />
        </motion.div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6"
          >
            <span className="inline-block px-4 py-2 bg-red-600/20 backdrop-blur-sm border border-red-500/30 rounded-full text-red-400 text-sm font-medium mb-8">
              Premium Car Service Since 1999
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Carz Street
            <br />
            <span className="bg-gradient-to-r from-red-500 via-red-600 to-red-700 bg-clip-text text-transparent">
              Multibrand Workshop
            </span>
          </motion.h1>

          {/* Kinetic Text Reveal */}
          <motion.p
            className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto font-light min-h-[60px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {displayText}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.8, repeat: Infinity, repeatType: 'reverse' }}
            >
              |
            </motion.span>
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <a href="tel:+918866689002">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg shadow-2xl hover:shadow-red-600/50 transition-all duration-300 group">
                Book Service Now: 8866689002
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg backdrop-blur-sm transition-all duration-300">
                Explore Services
              </Button>
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <motion.div
              className="w-1.5 h-3 bg-white rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview with Numbered Sections */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2 block">01 — Our Services</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Premium Services for
              <br />
              <span className="text-red-600">Premium Cars</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              return (
                <motion.div
                  key={service.id}
                  className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500" />
                  
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Link to="/services" className="inline-flex items-center text-red-600 font-semibold group-hover:gap-2 transition-all">
                      Learn More
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CSA - Roadside Assistance Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-900 to-red-900/40 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Heading + Contact */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center px-4 py-2 bg-red-600/20 backdrop-blur-sm border border-red-500/30 rounded-full text-red-400 text-sm font-semibold mb-6 uppercase tracking-wider">
                <span className="relative flex h-2.5 w-2.5 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                CSA Services &mdash; Available 24/7
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Roadside <span className="text-red-500">Assistance</span>
                <br />Anytime, Anywhere
              </h2>
              <p className="text-lg text-gray-300 mb-8 max-w-lg">
                Stuck on the road? Our Carz Street Assistance (CSA) team is just a call away, round the clock, to get you moving again.
              </p>

              <a
                href="tel:+918980003797"
                data-testid="csa-call-button-home"
                className="inline-flex items-center gap-4 bg-white rounded-2xl p-5 shadow-2xl hover:shadow-red-600/30 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">24/7 Helpline</div>
                  <div className="text-2xl font-bold text-gray-900">+91 89800 03797</div>
                </div>
              </a>

              {/* Annual Membership Plan */}
              <div className="mt-6 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 shadow-2xl max-w-md">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/20">
                  <div>
                    <div className="text-white/80 text-xs uppercase tracking-wider font-semibold">Annual Membership</div>
                    <div className="text-white text-lg font-bold">Road Assistance Plan</div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-white">&#8377;999</div>
                    <div className="text-white/80 text-xs">per year</div>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {[
                    'Battery Issues at cost',
                    'Towing Service with Crane at cost',
                    'Tyre Puncture Service at cost',
                    'Pick up & Drop for Service, Insurance & Lost Key at cost'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start text-white/95 text-sm">
                      <CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Right: Services List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { icon: BatteryCharging, label: 'Battery Issues' },
                  { icon: Truck, label: 'Towing Service with Crane*' },
                  { icon: CircleDot, label: 'Tyre Puncture Service' },
                  { icon: Fuel, label: 'Empty Fuel at cost (5 Litre)*' },
                  { icon: Car, label: 'Cab Service at Cost*' },
                  { icon: KeyRound, label: 'Pick up & Drop for Service, Insurance & Lost Key*' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                  >
                    <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-red-400" />
                    </div>
                    <span className="text-gray-200 font-medium text-sm leading-snug pt-1.5">{item.label}</span>
                  </motion.div>
                ))}
              </div>
              <p className="text-gray-400 text-xs italic mt-6 text-center">
                *Terms &amp; Conditions Applied
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brands Marquee */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800 overflow-hidden">
        <div className="mb-8 text-center">
          <span className="text-red-400 font-semibold text-sm tracking-wider uppercase">02 — Premium Brands We Service</span>
        </div>
        <motion.div
          className="flex space-x-16 whitespace-nowrap"
          animate={{ x: [0, -1000] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        >
          {[...brands, ...brands].map((brand, index) => (
            <span key={index} className="text-3xl font-bold text-white/20 hover:text-white/40 transition-colors">
              {brand}
            </span>
          ))}
        </motion.div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2 block">03 — Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Quote className="w-10 h-10 text-red-600 mb-4" />
                <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.content}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${['bg-red-600','bg-blue-600','bg-green-600','bg-purple-600'][index % 4]}`}>
                      {testimonial.name.replace(/\(.*?\)/g, '').trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose + FAQ (SEO-rich content) */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="text-red-600 font-semibold text-sm tracking-wider uppercase mb-2 block">Why Carz Street</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Dealership-Quality Car Service, Without the Dealership Price</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Carz Street is a trusted <strong>multi-brand car service workshop</strong> in Ahmedabad and Indore,
              handling everything from periodic service and <strong>wheel alignment</strong> to <strong>AC repair</strong>,
              <strong> ceramic coating</strong>, <strong>denting and painting</strong>, <strong>accident repair</strong>
              and <strong>insurance claim assistance</strong>. Over the past 26 years, our founder Mr. Vinod Parmar
              and his team have serviced more than 2 lakh vehicles — from small hatchbacks and sedans to premium
              European brands — with the same commitment: honest advice, factory-trained technicians and completely
              transparent pricing.
            </p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: 'Which car brands do you service at Carz Street?',
                a: 'We are a multi-brand workshop and service all major Indian and international brands including Maruti Suzuki, Hyundai, Tata, Mahindra, Honda, Toyota, Kia, Volkswagen, Škoda, Mercedes-Benz, BMW, Audi, Jaguar, Land Rover and more. Our technicians are certified across brands like Tata, Honda, Toyota and Hyundai.'
              },
              {
                q: 'What services are covered under the Carz Street AMC plan?',
                a: 'Our Comprehensive Maintenance Programme (₹18,999 for 1.5 years / 10,000 kms) covers periodic maintenance, engine oil & filter changes, HDS scanning, wheel alignment & balancing, AC service with gas top-up, radiator flushing, rubbing & polishing and interior cleaning. It also includes free breakdown assistance within city limits and 50% off labour charges.'
              },
              {
                q: 'Do you handle car insurance claims?',
                a: 'Yes. Carz Street is a tie-up partner with 15+ leading insurance companies including HDFC ERGO, TATA AIG, ICICI Lombard, Bajaj Allianz, Go Digit, SBI General, Cholamandalam MS and others. We handle the paperwork, direct billing and quick approval so your accident-repair claim is completely hassle-free.'
              },
              {
                q: 'Do you provide pickup and drop for car service?',
                a: 'Yes. Free pickup and drop is available for periodic service, insurance jobs and lost-key situations across Ahmedabad. Just call 88666 89002 and our team will arrange a convenient time slot.'
              },
              {
                q: 'Is roadside assistance available 24/7?',
                a: 'Yes. Our CSA (Carz Street Assistance) team is available 24×7 on 89800 03797 for battery jump-starts, towing with crane, tyre puncture, empty fuel delivery and emergency pickup within city limits.'
              },
              {
                q: 'Where are your workshops located?',
                a: 'Head office: Carz Street, PRL colony, Behind Minotti Furniture, Sindhu Bhavan Road, Bodakdev, Ahmedabad — 380054, Gujarat. Indore branch: Plot no. 30, New Bijalpur Main Road, Indore — 452012, MP. A third branch is coming soon at Vaishnodevi Circle (Tragad), Ahmedabad.'
              }
            ].map((item, i) => (
              <motion.details
                key={i}
                className="group bg-gray-50 rounded-2xl border border-gray-200 open:border-red-600 transition-colors"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between p-6 font-semibold text-gray-900">
                  <span>{item.q}</span>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white group-open:bg-red-600 group-open:text-white text-red-600 text-xl leading-none transition-colors flex-shrink-0 ml-4">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-700 leading-relaxed">{item.a}</div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-red-600 via-red-700 to-red-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience Premium Care?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Book your service today and let our experts take care of your premium vehicle.
            </p>
            <a href="tel:+918866689002">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg shadow-2xl transition-all duration-300 group">
                Book Your Service: 8866689002
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;